import {
  getCountriesListForCompany,
  getAllStatesForCountry,
  getAllCitiesForState,
  getShipmentModes
} from './Api';
import { setCacheForKey, getCacheForKey, deleteCacheForKeys } from './CacheManager';
import { getCachedOrder, getInquiryForProduct, getTotalPrice } from './Order';
import EventBus from './EventBus';
import { getFormattedPrice } from './locale';
import { findIndexInArray } from '../../../utils/GlobalUtils';
import isEqual from 'lodash.isequal';
import { getShippingConfiguration } from './Amalgam';
import { isShippingEnabled } from './Experiments';

let _selectedShippingMode = null;
const cachedShippingRateKeys = {};
let currentShippingRatesKey = null;
let fetchShippingModesRequest;

export const getSelectedShippingMode = () => _selectedShippingMode;

export const getSelectedShippingModeId = () => {
  const selectedShippingMode = getSelectedShippingMode();
  if (!selectedShippingMode) {
    return null;
  }
  return selectedShippingMode.shippingModeId;
};

export const setSelectedShippingMode = (selectedShippingMode = null) => {
  _selectedShippingMode = selectedShippingMode;
  EventBus.notify(EventBus.TYPE.SELECTED_SHIPPING_MODE_CHANGED, selectedShippingMode);
};

export const handleSelectedShippingMode = (newShippingModesList) => {
  if (Array.isArray(newShippingModesList) && newShippingModesList.length === 0) {
    setSelectedShippingMode();
    return;
  }

  const currentSelectedModeId = getSelectedShippingModeId();
  // if nothing is selected
  if (currentSelectedModeId === null) {
    setSelectedShippingMode(newShippingModesList[0]);
    return;
  }

  const selectedModeIndex = findIndexInArray(
    newShippingModesList,
    ({ shippingModeId }) => shippingModeId === currentSelectedModeId
  );

  if (selectedModeIndex === -1) {
    setSelectedShippingMode(newShippingModesList[0]);
  }
};

const fetchCoutriesDataObject = {};
const fetchCoutriesDataPromises = {};
const COUNTRIES_LIST = 'COUNTRIES_LIST';

const createKeyForCountriesList = (companyId) => `${COUNTRIES_LIST}_${companyId}`;

export const getCountriesListForSpecificCompany = (companyId) => {
  const key = createKeyForCountriesList(companyId);

  if (fetchCoutriesDataObject[key]) {
    return Promise.resolve(fetchCoutriesDataObject[key]);
  }

  if (fetchCoutriesDataPromises[key]) {
    return fetchCoutriesDataPromises[key];
  }

  fetchCoutriesDataPromises[key] = new Promise((resolve, reject) => {
    getCountriesListForCompany(companyId)
      .then((response) => {
        fetchCoutriesDataObject[key] = response.countries;
        resolve(response.countries);
      })
      .catch((error) => reject(error));
  });
  return fetchCoutriesDataPromises[key];
};

export const createCacheKeyForStateList = ({ countryId, stateId }) => `${countryId}-${stateId}`;

export const createCacheKeyForShippingModes = ({ countryId, stateId, cityId }) =>
  `${countryId}-${stateId}-${cityId}`;

export const getCachedStateListForCountry = (country) => getCacheForKey(country);

export const getStatesForCountry = (countryId) =>
  new Promise((resolve, reject) => {
    const stateList = getCachedStateListForCountry(countryId);
    if (stateList) {
      resolve(stateList);
      return;
    }

    getAllStatesForCountry(countryId)
      .then(({ allStates }) => {
        setCacheForKey({ key: countryId, value: allStates });
        resolve(allStates);
      })
      .catch((error) => reject(error));
  });

export const getCachedCityListForState = (state) => getCacheForKey(state);

export const getCitiesForState = ({ countryId, stateId }) => {
  const cacheKey = createCacheKeyForStateList({ countryId, stateId });
  return new Promise((resolve, reject) => {
    const cityList = getCachedCityListForState(cacheKey);
    if (cityList) {
      resolve(cityList);
      return;
    }

    getAllCitiesForState(countryId, stateId)
      .then(({ allCities }) => {
        setCacheForKey({ key: cacheKey, value: allCities });
        resolve(allCities);
      })
      .catch((error) => reject(error));
  });
};

export const getCachedShippingModes = (shippingModeKey) => getCacheForKey(shippingModeKey);

export const getActiveCachedShippingModes = () => getCachedShippingModes(currentShippingRatesKey);

export const getPreviousShippingModesParamsCacheKey = () => 'PREVIOUS_SHIPPING_MODES_PARAMS_KEY';

export const calculateProductsWeight = ({ selectedProducts } = {}) => {
  let productWeights = 0;
  Object.keys(selectedProducts || {}).forEach((productId) => {
    const inquiry = getInquiryForProduct(productId);
    if (!inquiry) {
      return;
    }
    const { product, itemCount } = inquiry;
    const productWeight = product.weight * itemCount;
    productWeights += productWeight;
  });
  return productWeights;
};

export const isPincodeRequiredField = (selectedCountryId) => {
  const { pincode = {} } = getShippingConfiguration();
  return selectedCountryId === 'IN' && pincode.required;
};

/**
 * Checks the validity of pincode
 * @param {String} pincode
 * @returns following values
 *        ''    -> NaN
 *        false -> invalid
 *        true  -> valid
 */
export const isValidPincode = (pincode = '') => {
  const pincodeNumber = Number.parseInt(pincode, 10);
  if (Number.isNaN(pincodeNumber)) {
    return '';
  }
  // checking for 6 digit value
  if (pincodeNumber < 1 || pincodeNumber > 999999 || pincode.length !== 6) {
    return false;
  }
  return true;
};

// checks if shipping address or billing detail (if provided) have required value;
export const getInvalidFieldsListFromShippingConfig = (
  { countryId, stateId, pincode, city: cityName, state: stateName } = {},
  validateBillingDetails,
  visitorBillingDetails,
  captureShippingAddress
) => {
  const { country, state, city, email: emailFieldConfig } = getShippingConfiguration();

  const invalidFields = [];
  // if required field check for value
  if (validateBillingDetails && emailFieldConfig && emailFieldConfig.required) {
    const { email, emailInvalid } = visitorBillingDetails;

    if (!email || emailInvalid) {
      invalidFields.push('EMAIL');
    }
  }

  if (!captureShippingAddress && !isShippingEnabled()) {
    return invalidFields;
  }

  if (country && country.required) {
    if (!countryId) {
      invalidFields.push('COUNTRY');
    }
  }

  if (state && state.required) {
    if (!stateId && !stateName) {
      invalidFields.push('STATE');
    }
  }

  if (city && city.required) {
    // city name needs to be entered if required
    // case 1: citymissing - cityname needs to enter
    // case 2: city is selected - city name will be selected
    if (!cityName) {
      invalidFields.push('CITY');
    }
  }

  if (isPincodeRequiredField(countryId)) {
    if (!isValidPincode(pincode)) {
      invalidFields.push('PINCODE');
    }
  }

  return invalidFields;
};

export const clearShipmentModesParamsCacheData = () => {
  const previousParamskey = getPreviousShippingModesParamsCacheKey();
  setCacheForKey({ key: previousParamskey, value: null });
};

export const getSelectedPaymentOption = ({ paymentMode, additionalPaymentMode }) =>
  paymentMode === 'PARTIAL_COD' ? additionalPaymentMode : paymentMode;

const isCashOnDeliveryMode = ({ paymentMode }) =>
  paymentMode === 'PARTIAL_COD' || paymentMode === 'CASH_ON_DELIVERY';

export const getPaymentOptionForShippingCost = ({ paymentMode }) => paymentMode;

export const isPaymentModeRequired = ({ paymentMethods } = {}) =>
  Object.keys(paymentMethods || {}).length && getOrderPriceWithShipping();

export const getAvailableShippingModes = (shippingDetails) =>
  new Promise((resolve, reject) => {
    const {
      countryId,
      stateId,
      cityId,
      pincode = '',
      cityMissing,
      city: cityName,
      weight,
      paymentOption,
      paymentMode,
      orderPrice
    } = shippingDetails;
    const cacheKey = createCacheKeyForShippingModes(shippingDetails);

    const previousShippingParamsCacheKey = getPreviousShippingModesParamsCacheKey();
    const previousShippingParams = getCacheForKey(previousShippingParamsCacheKey);
    const newShippingModesParams = {
      countryId,
      stateId,
      cityId: cityMissing ? cityName : cityId,
      pincode,
      paymentOption: isCashOnDeliveryMode({ paymentMode }),
      weight,
      orderPrice
    };

    if (isEqual(newShippingModesParams, previousShippingParams)) {
      const shippingModes = getCachedShippingModes(cacheKey);
      if (shippingModes) {
        return resolve(shippingModes);
      }
      return resolve();
    }

    // Cache the cache key, so that all the caches can be deleted when required
    cachedShippingRateKeys[cacheKey] = true;
    // Save the current key so that the cache can be accessed from anywhere
    currentShippingRatesKey = cacheKey;
    // caching the shipping params to avoid extra api calls for same params
    setCacheForKey({ key: previousShippingParamsCacheKey, value: newShippingModesParams });

    if (fetchShippingModesRequest && fetchShippingModesRequest.cancel) {
      fetchShippingModesRequest.cancel();
    }

    EventBus.notify(EventBus.TYPE.FETCH_SHIPPING_DETAILS_UPDATED, { loading: true });
    getShipmentModes(
      { ...newShippingModesParams, cityId, paymentOption },
      fetchShippingModesRequest
    )
      .then((shippingModesList) => {
        setCacheForKey({ key: cacheKey, value: shippingModesList });
        handleSelectedShippingMode(shippingModesList);
        EventBus.notify(EventBus.TYPE.FETCH_SHIPPING_DETAILS_UPDATED, { loading: false });
        resolve(shippingModesList);
      })
      .catch((error) => {
        if (error && error.message === 'Cancelled') {
          return;
        }
        EventBus.notify(EventBus.TYPE.FETCH_SHIPPING_DETAILS_UPDATED, { loading: false, error });
        reject(error);
      });
  });

export const getIdForSelectedAddress = ({ addressName, addressList } = {}) => {
  if (!addressName || !Array.isArray(addressList)) {
    return;
  }

  const lowerCasedAddressName = addressName.toLowerCase();
  const index = findIndexInArray(
    addressList,
    ({ name }) => name.toLowerCase() === lowerCasedAddressName
  );
  if (index === -1) {
    return null;
  }
  return addressList[index];
};

export const getShippingCost = (shippingMode) => {
  const { rate, freeShippingAmount } = shippingMode;
  const orderAmount = getTotalPrice();
  if (typeof freeShippingAmount === 'number' && orderAmount >= freeShippingAmount) {
    return 0;
  }

  const order = getCachedOrder();
  if (!order) {
    return null;
  }

  return rate;
};

export const getOrderPriceWithShipping = () => {
  const orderPrice = getTotalPrice();
  const shippingMode = getSelectedShippingMode();
  if (!shippingMode || typeof shippingMode.price !== 'number') {
    return orderPrice;
  }

  return orderPrice + shippingMode.price;
};

export const getFormattedOrderPriceWithShipping = () =>
  getFormattedPrice(getOrderPriceWithShipping());

export const doesOrderHaveWeights = () => {
  const order = getCachedOrder();
  if (!order) {
    return false;
  }

  let weightsExist = false;
  for (const productId of Object.keys(order.selectedProducts || {})) {
    const inquiry = getInquiryForProduct(productId);
    if (!inquiry) {
      continue;
    }

    if (inquiry.product.weight && typeof inquiry.product.weight === 'number') {
      weightsExist = true;
      break;
    }
  }

  return weightsExist;
};

export const getFirstEligibleShippingMode = (shipmentModesArray) => {
  let firstEligibleMode = null;
  for (const shipmentModeData of shipmentModesArray) {
    const { shippingModeName } = shipmentModeData;
    const shippingModeCost = getShippingCost(shipmentModeData);
    // Skip this shipment mode if shipping cost is not present
    if (typeof shippingModeCost !== 'number') {
      continue;
    }
    // Shipping cost present and mode is standard, selection matched
    if (shippingModeName === 'Standard') {
      firstEligibleMode = shipmentModeData;
      break;
    }

    // Continue searching for standard and select it if available
    if (firstEligibleMode === null) {
      firstEligibleMode = shipmentModeData;
    }
  }
  return firstEligibleMode;
};

export const getUpdatedSelectedModeDataIfEligible = (shipmentModesArray) => {
  const selectedShippingMode = getSelectedShippingMode();
  if (!selectedShippingMode) {
    return null;
  }

  let updatedShippingModeData = null;
  for (const shipmentModeData of shipmentModesArray) {
    const { shippingModeId } = shipmentModeData;
    const shippingModeCost = getShippingCost(shipmentModeData);
    // Search until the current shipping mode id matches
    if (shippingModeId !== selectedShippingMode.shippingModeId) {
      continue;
    }

    /*
      Selected shipping mode matched and it has the correct cost, return
      the updated shipping mode data. If the shipping mode no longer has
      a cost because the order is falling out of the weight profiles, then
      return null to indicate that the shipping mode is no longer valid
    */
    if (typeof shippingModeCost === 'number') {
      updatedShippingModeData = shipmentModeData;
    }
    break;
  }

  return updatedShippingModeData;
};

export const deleteCachedShippingRates = () => {
  for (const key in cachedShippingRateKeys) {
    if (!Object.prototype.hasOwnProperty.call(cachedShippingRateKeys, key)) {
      continue;
    }

    deleteCacheForKeys([key]);
    delete cachedShippingRateKeys[key];
  }
  currentShippingRatesKey = null;
  clearShipmentModesParamsCacheData();
};

export const processSelectedShippingMode = ({
  onUpdateShippingMode = setSelectedShippingMode,
  shipmentModes
}) => {
  if (!Array.isArray(shipmentModes)) {
    return;
  }

  const updatedShippingModeData = getUpdatedSelectedModeDataIfEligible(shipmentModes);
  if (updatedShippingModeData !== null) {
    onUpdateShippingMode({
      ...updatedShippingModeData,
      price: getShippingCost(updatedShippingModeData)
    });
    return;
  }

  const firstEligibleMode = getFirstEligibleShippingMode(shipmentModes);
  if (firstEligibleMode === null) {
    onUpdateShippingMode();
    return;
  }

  onUpdateShippingMode({
    ...firstEligibleMode,
    price: getShippingCost(firstEligibleMode)
  });
};
